//services
import API from 'services/api.services';
//constants
import {
	API_ADD_BALANCE_REQUEST,
	API_CREATE_ADD_DIGITAL_BALANCE,
	API_GET_ALTSHULER_SUGGESTED_ACCOUNT,
	API_GET_COMPANY_BALANCES,
	API_SEND_WITHDRAWAL_REQUEST,
	API_GET_WITHDRAWAL_REQUESTS,
	API_HAS_RECENT_DIGITAL_BALANCE_ADDITIONS,
} from 'constants/db.constants';
import FormData from 'form-data';

//reducers
import {
	resetAddBalanceProcess,
	setActiveStep,
	setLoading,
	setSteps,
	setSuggestedAccountId,
	setCompanyBalances,
	setWithdrawalSuccess,
	setMessage,
	setDigitalBalanceFeatureState,
	showMobilePopup,
	showComputerPopup,
	showErrorPopup,
} from 'state/reducers/balance.reducers';
import { balanceSteps, balanceStepsStatus, DigitalBalanceFeatureState } from 'constants/balance.constants';
import _ from 'lodash';
import { store } from 'state/store';
import { SupportType } from 'constants/contact.constants';
import { getBalancesActivityByCompanyId, getDailyDigitalBalance } from './activity.services';
import * as globalServices from './global.services';
import { getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';
import { getDigitalDepositBankAccountsAvailabilitySummary } from './bank-account.services';
import { setErrorMessage } from 'utils';
import { getDomainSettings } from 'state/reducers/system_settings.reducers';

export function validateStep1() {
	return (dispatch: any) => {
		dispatch(
			setSteps({
				[balanceSteps.BankAccount]: {
					status: balanceStepsStatus.Completed,
				},
				[balanceSteps.Currency]: {
					status: balanceStepsStatus.InProgress,
				},
			})
		);
		dispatch(setActiveStep(balanceSteps.Currency));
		return;
	};
}

export function validateStep2() {
	return async (dispatch: any) => {
		dispatch(
			setSteps({
				[balanceSteps.Currency]: {
					status: balanceStepsStatus.Completed,
				},
				[balanceSteps.TransferDetails]: {
					status: balanceStepsStatus.InProgress,
				},
			})
		);
		dispatch(setActiveStep(balanceSteps.TransferDetails));
		return;
	};
}
export function validateStep3(
	companyBankAccountId: number,
	altshulerBankAccountId: number,
	transferAmount: string,
	file: File
) {
	return async (dispatch: any) => {
		try {
			dispatch(setLoading(true));

			const formData = new FormData();

			formData.append('companyId', store.getState().user.details?.companyId.toString());
			formData.append('bankAccountId', String(companyBankAccountId));
			formData.append('altshulerAccountId', String(altshulerBankAccountId));
			formData.append('amount', Number(transferAmount));
			formData.append('file', file, file.name);

			const response = await API.getInstance().post(API_ADD_BALANCE_REQUEST, formData);

			dispatch(
				setSteps({
					[balanceSteps.TransferDetails]: {
						status: balanceStepsStatus.Completed,
					},
				})
			);
			dispatch(
				setSteps({
					[balanceSteps.TransferDetails]: {
						status: balanceStepsStatus.Completed,
					},
				})
			);
			dispatch(setActiveStep(balanceSteps.Complete));
		} catch (error: any) {
			setErrorMessage(setMessage, error);
		} finally {
			dispatch(setLoading(false));
		}

		return;
	};
}

export function createAddDigitalBalance(
	toContinueOnMobile: boolean,
	userBankAccountDetails: {
		bankId: number;
		branchNumber: string;
		accountNumber: string;
	},
	amount: number,
	currencyId: number
) {
	return async (dispatch: any) => {
		try {
			dispatch(setLoading(true));
			const requestData = {
				toContinueOnMobile,
				userBankAccountDetails,
				amount,
				currencyId,
			};

			const response = await API.getInstance().post(API_CREATE_ADD_DIGITAL_BALANCE, requestData);
			const responseData = response.data;

			if (toContinueOnMobile) {
				// Mobile flow
				if (responseData.success) {
					dispatch(setLoading(false));
					dispatch(showMobilePopup());
				} else {
					dispatch(
						showErrorPopup({
							error: {
								message: responseData.message || 'Mobile transfer failed',
								data: { message: responseData.message || 'Mobile transfer was not successful' },
							},
							retryData: requestData,
						})
					);
				}
			} else {
				// Desktop flow: check for link
				if (responseData.link) {
					window.location.replace(responseData.link);
					//TODO:remove the computer popup if no longer needed
					//dispatch(showComputerPopup());
				} else {
					dispatch(setLoading(false));
					dispatch(
						showErrorPopup({
							error: {
								message: responseData.message || 'Unable to generate payment link',
								data: { message: responseData.message || 'Unable to generate payment link' },
							},
							retryData: requestData,
						})
					);
				}
			}

			return responseData;
		} catch (error: any) {
			dispatch(setLoading(false));
			throw error;
		}
	};
}

export function summarizeBalanceProcess() {
	const paramsSetting = {};
	return async (dispatch: any) => {
		dispatch(resetAddBalanceProcess());

		dispatch(
			setSteps({
				[balanceSteps.BankAccount]: {
					status: balanceStepsStatus.NotStarted,
				},
				[balanceSteps.Currency]: {
					status: balanceStepsStatus.NotStarted,
				},
				[balanceSteps.TransferDetails]: {
					status: balanceStepsStatus.NotStarted,
				},
			})
		);
		dispatch(setActiveStep(balanceSteps.BankAccount));
	};
}

export function getSuggestedAltshulerAccountByCompanyId() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		const paramsSetting = {
			companyId: store.getState().user.details?.companyId,
		};

		await API.getInstance()
			.get(API_GET_ALTSHULER_SUGGESTED_ACCOUNT, paramsSetting)
			.then((response) => {
				const { suggestedAccountId } = response.data;
				dispatch(setSuggestedAccountId(suggestedAccountId));
				dispatch(setLoading(false));
			})
			.catch((error) => {
				dispatch(setLoading(false));
				dispatch(setSuggestedAccountId(null));
				setErrorMessage(setMessage, error);
			});
	};
}

export function cancelAddBalanceProcess() {
	return async (dispatch: any) => {
		dispatch(resetAddBalanceProcess());
	};
}

export function getBalancesByCompanyId() {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		const paramsSetting = {
			companyId: store.getState().user.details?.companyId,
		};

		await API.getInstance()
			.get(API_GET_COMPANY_BALANCES, paramsSetting)
			.then((response) => {
				dispatch(setCompanyBalances(response.data.balances));
				dispatch(setLoading(false));
			})
			.catch((error) => {
				dispatch(setLoading(false));
				dispatch(setCompanyBalances([]));
				setErrorMessage(setMessage, error);
			});
	};
}

export function sendWithdrawalRequest(
	subject: string,
	message: string,
	type: SupportType,
	currency: string,
	amount: number,
	companyBankAccountId: number
) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		const companyId = store.getState().user.details?.companyId;
		const paramsSetting = {
			companyId,
			subject,
			message,
			type,
			currency,
			amount,
			companyBankAccountId,
		};
		await API.getInstance()
			.post(API_SEND_WITHDRAWAL_REQUEST, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				dispatch(setWithdrawalSuccess(true));
				dispatch(getBalancesByCompanyId());
				dispatch(getBalancesActivityByCompanyId(Number(companyId)));
			})
			.catch((error) => {
				setTimeout(() => {
					dispatch(setLoading(false));
					dispatch(setWithdrawalSuccess(false));
					setErrorMessage(setMessage, error);
				}, 500);
			});
	};
}

export function getWithdrawalRequest(type: SupportType[]) {
	return async (dispatch: any) => {
		dispatch(setLoading(true));
		const paramsSetting = {
			companyId: store.getState().user.details?.companyId,
			type,
		};
		await API.getInstance()
			.post(API_GET_WITHDRAWAL_REQUESTS, paramsSetting)
			.then((response) => {
				dispatch(setLoading(false));
				dispatch(setWithdrawalSuccess(response.data.supportRequests.length ? true : false));
			})
			.catch((error) => {
				setTimeout(() => {
					dispatch(setLoading(false));
					dispatch(setWithdrawalSuccess(false));
					setErrorMessage(setMessage, error);
				}, 500);
			});
	};
}

export function isDigitalDepositAllowed() {
	return async (dispatch: any) => {
		const rootState = store.getState();
		const balanceSettings = getDomainSettings('balance')(rootState);
		const { digitalBalanceFeatureEnabled = false, maxAmountToDigitalDeposit = 0 } = balanceSettings;

		const userPermission = getUserPermission(rootState);
		const maxAmount = Number.isNaN(Number(maxAmountToDigitalDeposit)) ? 0 : Number(maxAmountToDigitalDeposit);
		const isOverLimit = getDailyDigitalBalance() >= maxAmount;
		const { supportedDigitalDepositBankAccounts, availableDigitalDepositBankAccounts } =
			getDigitalDepositBankAccountsAvailabilitySummary();

		if (!Boolean(digitalBalanceFeatureEnabled))
			return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.FeatureDisabled));
		if (userPermission !== UserPermission.SIGNER)
			return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.UserNotAuthorized));
		if (isOverLimit) return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.AmountOutOfRange));
		if (supportedDigitalDepositBankAccounts.length === 0)
			return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.BankNotSupported));
		if (availableDigitalDepositBankAccounts.length === 0)
			return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.AllBanksTemporarilyUnavailable));

		return dispatch(setDigitalBalanceFeatureState(DigitalBalanceFeatureState.Enabled));
	};
}

export async function hasRecentDigitalBalanceAdditions(): Promise<boolean> {
	try {
		const response = await API.getInstance().get(API_HAS_RECENT_DIGITAL_BALANCE_ADDITIONS);
		if (response?.data?.success === true && typeof response.data.hasDigitalBalanceAdditions === 'boolean') {
			return response.data.hasDigitalBalanceAdditions;
		}
		return false;
	} catch {
		return false;
	}
}
